<mat-progress-bar mode="determinate" [value]="progress" />

<h2 mat-dialog-title>Register Shopping</h2>

<mat-dialog-content class="mat-typography">
  <mat-stepper linear #stepper>
    <mat-step [completed]="shoppingSession" [stepControl]="sessionForm.formGroup">
      <ng-template matStepLabel>
        @if (shoppingSession) {
          {{ shoppingSession.date | date: 'd MMMM yyyy, HH:mm' }}
          at {{ shoppingSession.store?.name }} for {{ shoppingSession.total | svCurrency }}
        } @else if (sessionForm.isValid) {
          {{ sessionForm.controls.date.value | date: 'd MMMM yyyy, HH:mm' }}
          at {{ sessionForm.controls.store.value?.name }} for
          {{ sessionForm.controls.total.value | svCurrency }}
        } @else {
          Session
        }
      </ng-template>

      <form class="mt-2" [formGroup]="sessionForm.formGroup">
        <div class="row">
          <div class="col-lg-6 col-md-12">
            <mat-form-field>
              <mat-label>Shopping Session</mat-label>
              <mat-select
                [formControl]="sessionForm.controls.existingShoppingSession"
                (selectionChange)="selectedShoppingSession()">
                <mat-select-trigger>
                  <ng-container *ngIf="sessionForm.controls.existingShoppingSession.value as selected">
                    {{ selected?.date | date: 'd MMMM yyyy, HH:mm' : '' : 'en-GB' }} at {{ selected?.store?.name }} for
                    {{ selected?.total | svCurrency }}
                  </ng-container>
                </mat-select-trigger>

                <mat-option [value]="null">-</mat-option>

                @for (option of sessionForm.options.shoppingSessions; track $index) {
                  <mat-option [value]="option">
                    <div class="expanded-select-option">
                      <p>
                        {{ option.date | date: 'd MMMM yyyy, HH:mm' }}
                      </p>

                      <p>
                        <small class="detail">
                          <i class="fa fa-location-dot small-icon"></i>
                          {{ option.store?.name }}
                        </small>

                        <small class="detail">
                          @if (option.total && option.total > 0) {
                            <i class="fa fa-coins small-icon"></i>
                            {{ option.total | svCurrency }}
                          }
                        </small>
                      </p>
                    </div>
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <div class="row">
          @if (!sessionForm.useExisting) {
            <div class="col-xxl-2 col-xl-4 col-lg-6 col-md-12">
              <mat-form-field>
                <mat-label>Store</mat-label>
                <mat-select [formControl]="sessionForm.controls.store">
                  @for (option of sessionForm.options.stores; track $index) {
                    <mat-option [value]="option">
                      {{ option.name }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            <div class="col-xxl-4 col-xl-8 col-lg-6 col-md-12">
              <app-date-time-input [control]="sessionForm.controls.date" label="Date" />
            </div>

            <div class="col-xxl-2 col-lg-4 col-md-4 col-sm-6">
              <app-number-input [control]="sessionForm.controls.total" label="Total" suffix="kr" />
            </div>

            <div class="col-xxl-2 col-lg-4 col-md-4 col-sm-6">
              <app-number-input [control]="sessionForm.controls.bonus" label="Bonus" suffix="kr" />
            </div>

            <div class="col-xxl-2 col-lg-4 col-md-4 col-sm-6">
              <app-number-input [control]="sessionForm.controls.excluded" label="Excluded" suffix="kr" />
            </div>
          }
        </div>
      </form>
    </mat-step>

    <mat-step [stepControl]="productForm.formGroup">
      <ng-template matStepLabel>
        @if (productForm.isValid) {
          {{ productForm.controls.product.value }}
        } @else {
          Product
        }
      </ng-template>

      <div class="row">
        <div class="col-12 mb-3" [ngClass]="{ 'col-xl-6 col-lg-12': product && recentActions.length }">
          <form class="mt-2" [formGroup]="productForm.formGroup">
            <app-dashboard-product-select [hideNotInStock]="false" />
          </form>

          @if (product) {
            <form [formGroup]="itemForm.formGroup">
              <div class="row">
                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>Amount</mat-label>

                    <input matInput [formControl]="itemForm.controls.amount" type="number" />

                    <span matSuffix class="d-flex">
                      <span class="input-text-suffix">{{ itemForm.purchaseQuantityUnitName }}</span>
                    </span>

                    <mat-hint>{{ itemForm.newStock }}</mat-hint>
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <app-date-input
                    [control]="itemForm.controls.bestBeforeDate"
                    label="Best Before"
                    [toggleControl]="itemForm.controls.neverOverdue" />
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>{{ itemForm.purchaseQuantityUnit?.description }} Price</mat-label>

                    <input matInput [formControl]="itemForm.controls.purchaseQuPrice" type="number" />

                    <span matSuffix class="d-flex">
                      <span class="input-text-suffix">kr</span>
                    </span>
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>
                      Price for {{ itemForm.amount }} {{ itemForm.purchaseQuantityUnitName | titlecase }}
                    </mat-label>

                    <input matInput [formControl]="itemForm.controls.totalPrice" type="number" />

                    <span matSuffix class="d-flex">
                      <span class="input-text-suffix">kr</span>
                    </span>

                    <mat-hint>
                      {{ itemForm.amount }} * {{ itemForm.purchaseQuantityUnitPriceWithoutPant | svCurrency }} =
                      {{ itemForm.totalPriceWithoutDiscount | svCurrency }}
                    </mat-hint>
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>
                      {{ itemForm.purchaseQuantityUnit?.description }}
                      Pant
                    </mat-label>

                    <input matInput [formControl]="itemForm.controls.purchaseQuPant" type="number" />

                    <div matSuffix class="d-flex align-items-center">
                      <span class="me-3">kr</span>
                      <mat-slide-toggle [formControl]="itemForm.controls.pantIncluded">Included</mat-slide-toggle>
                    </div>

                    @if ((itemForm.purchaseQuantityUnitPant || 0) > 0 && itemForm.pantIncluded) {
                      <mat-hint>
                        {{ itemForm.amount }} * {{ itemForm.purchaseQuantityUnitPant | svCurrency }} =
                        {{ itemForm.totalPant | svCurrency }}
                      </mat-hint>
                    }
                  </mat-form-field>
                </div>

                <div class="col-xxl-6 col-xl-12 col-lg-6 col-md-6 col-sm-12">
                  <mat-form-field>
                    <mat-label>Total Discount</mat-label>

                    <input matInput [formControl]="itemForm.controls.totalDiscount" type="number" />

                    <span matSuffix class="d-flex">
                      <span class="input-text-suffix">kr</span>
                    </span>

                    @if (itemForm.totalDiscount && itemForm.totalDiscount > 0) {
                      <mat-hint>
                        {{ itemForm.totalPriceWithoutDiscount | svCurrency }}
                        - {{ itemForm.totalDiscount | svCurrency }} =
                        {{ itemForm.totalPrice | svCurrency }}
                      </mat-hint>
                    }
                  </mat-form-field>
                </div>
              </div>
            </form>
          }
        </div>

        @if (product && recentActions.length) {
          <div class="col-xl-6 col-lg-12">
            <div class="mt-3 ms-3">
              <app-dashboard-recent-actions />
            </div>
          </div>
        }
      </div>
    </mat-step>
  </mat-stepper>

  @if (dataSource.data.length) {
    <div class="table-container table-container-full-height ps-4 pe-4 m-0">
      <h3>Receipt</h3>
      <table mat-table [dataSource]="dataSource">
        <ng-container matColumnDef="product">
          <th mat-header-cell *matHeaderCellDef>Product</th>
          <td mat-cell *matCellDef="let row">{{ row.productCategoryName }} • {{ row.productName }}</td>
          <td mat-footer-cell *matFooterCellDef>
            <b>Total</b>
          </td>
        </ng-container>

        <ng-container matColumnDef="breakdown">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let row">
            @if (row.amount > 1) {
              <div class="registered-purchase-details">
                {{ row.amount }} * {{ row.price + row.discount | svCurrency }}
              </div>
            }

            @if (row.discount > 0) {
              <div>&nbsp;</div>
            }

            @if (row.pant > 0) {
              <div class="registered-purchase-details">{{ row.amount }} * {{ row.pant | svCurrency }} (PANT)</div>
            }
          </td>
          <td mat-footer-cell *matFooterCellDef></td>
        </ng-container>

        <ng-container matColumnDef="price">
          <th mat-header-cell *matHeaderCellDef>Price</th>
          <td mat-cell *matCellDef="let row">
            @if (row.price !== null) {
              <b>{{ row.amount * (row.price + row.discount) | svCurrency }}</b>
            }

            @if (row.discount > 0) {
              <div class="registered-purchase-discount">- {{ row.amount * row.discount | svCurrency }}</div>
            }

            @if (row.pant > 0) {
              <div class="registered-purchase-pant">+ {{ row.amount * row.pant | svCurrency }}</div>
            }
          </td>
          <td mat-footer-cell *matFooterCellDef>
            <b>{{ priceProgress | svCurrency }}</b>
          </td>
        </ng-container>

        <ng-container matColumnDef="undo">
          <th mat-header-cell *matHeaderCellDef stickyEnd></th>
          <td mat-cell *matCellDef="let row">
            <button
              mat-icon-button
              (click)="undoPurchase(row.id)"
              [disabled]="isSubmitting"
              matTooltip="Undo Purchase"
              matTooltipPosition="right">
              <mat-icon>undo</mat-icon>
            </button>
          </td>
          <td mat-footer-cell *matFooterCellDef></td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columns"></tr>
        <tr mat-row *matRowDef="let row; columns: columns"></tr>
        <tr mat-footer-row *matFooterRowDef="columns"></tr>
      </table>
    </div>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <div class="row mt-3">
    <div class="col text-end">
      @if (stepper.selectedIndex === 1) {
        <button
          mat-raised-button
          color="primary"
          (click)="registerPurchase()"
          [disabled]="itemForm.formGroup.invalid || !itemForm.product || isSubmitting">
          <mat-icon>done</mat-icon>
          Register
        </button>
      }

      @if (sessionForm.useExisting) {
        <a
          mat-raised-button
          mat-dialog-close
          [routerLink]="links.shoppingSessions.viewOne(sessionForm.controls.existingShoppingSession.value!.id)">
          <mat-icon>shopping_cart</mat-icon>
          View Shopping Session
        </a>
      } @else {
        @if (!sessionIsValid) {
          <button mat-raised-button (click)="selectLatestShoppingSession()" [disabled]="isSubmitting">
            <mat-icon>history</mat-icon>
            Latest
          </button>
        }

        <button mat-raised-button (click)="createShoppingSession()" [disabled]="!sessionIsValid || isSubmitting">
          <mat-icon>done</mat-icon>
          Create Shopping Session
        </button>
      }

      @if (itemForm) {
        <app-open-form-validation-errors-dialog [formGroup]="itemForm.formGroup" />
      }

      <button mat-raised-button mat-dialog-close>
        <mat-icon>close</mat-icon>
        Close
      </button>
    </div>
  </div>
</mat-dialog-actions>
